import React, { useMemo } from "react";
import { get } from "lodash";
import classNames from "classnames";
import "./Table.scss";
import Icon from "../Icon";
import { sortByNumberOrString } from "./sorting";
import useWindowSize from "../../hooks/useWindowSize";


const Table = ({
  columns,
  data,
  defaultSort,
  stickyHeader = false,
  headerTop,
  expandedGroups = [],
  applyMinWidth = false,
}) => {
  const [sort, setSort] = React.useState(
    defaultSort || {
      accessor: "",
      direction: "",
    }
  );

  const {isMobile} = useWindowSize();

  const { accessor, direction } = sort;

  const headerStyle = (style) => {
    return {
      ...style,
      top: headerTop ?? style.top ?? undefined,
    };
  };

  const sortingAlgorithm =
    columns.find((c) => c.accessor === accessor)?.sortingAlgorithm ||
    sortByNumberOrString;

  const sorted = useMemo(() => {
    const sortedData = data.slice().sort((a, b) => {
      const valA = get(a, accessor);
      const valB = get(b, accessor);
      return sortingAlgorithm(valA, valB);
    });

    return direction === "ascending" ? sortedData.reverse() : sortedData;
  }, [data, accessor, sortingAlgorithm, direction]);

  const minWidth = applyMinWidth
      ? columns.reduce((filtered, column) => {
          column.minWidth && filtered.push(column.minWidth);
          return filtered;
        }, [])
      : [];

  return (
    <table className="table"
      style={{ minWidth: !isMobile ? (`calc(${minWidth.join(" + ")})`) : null }}
    >
      <thead className="table__head">
        <tr className="table__row table__row__fixed">
          {columns.map((c, i) => (
            <th
              key={`table-header-${i}`}
              className={classNames(
                "table__header",
                c.label === "Action" && "text-center",
                {
                  "table__header--center": c.center,
                  "table__header--hide-xs": c.xsHide,
                  "table__header--sticky": stickyHeader,
                }
              )}
              style={headerStyle(c.style ?? {})}
            >
              {c.sortable ? (
                <button
                  className="table__sort"
                  onClick={() =>
                    setSort({
                      accessor: c.accessor,
                      direction:
                        direction === "ascending" ? "descending" : "ascending",
                    })
                  }
                >
                  {c.label}
                  {c.accessor === accessor && (
                    <Icon
                      glyph={
                        direction === "ascending" ? "caret-up" : "caret-down"
                      }
                      weight="solid"
                    />
                  )}
                </button>
              ) : (
                c.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="table__body">
        {sorted.map((d, i) => {
          if (!d.isGroup) {
            return <TableRow data={d} columns={columns} index={i} />;
          } else {
            return (
              <TableGroupRow
                data={d}
                columns={columns}
                index={i}
                isExpanded={expandedGroups.includes(d.id)}
                isMobile={isMobile}
              />
            );
          }
        })}
      </tbody>
    </table>
  );
};

const TableRow = ({ data, columns, index, style, emphasis=false }) => (
  <tr key={`table-row-container${index}`} className={classNames("table__row", {"table__row--emphasis": emphasis})} style={style}>
    {columns.map((c, x) => (
      <td
        key={`table-td=${x}-${data.id}-${index}`}
        className={classNames(
          "table__data",
          c.label === "Action" && "text-center",
          {
            "table__data--center": c.center,
            "table__data--hide-xs date": c.xsHide,
            "table__data--text-overflow": c.textOverflow,
          }
        )}
        style={c.label === "File Name" ? { width: "40%" } : {}}
      >
        {c.cell ? c.cell(data) : get(data, c.accessor)}
      </td>
    ))}
  </tr>
);

const TableGroupRow = ({ data, columns, index, style, isExpanded, isMobile }) => {


  return (
    <React.Fragment key={`group-header-container-${index}`}>
      <tr key={"group-header-"+data.id} className={classNames("table__row", {"table__row--emphasis": isExpanded})} style={style}>
        {columns.map((c, x) =>
          c.groupedHeaderCell ? (
            <td
              key={`group-td=${x}-${data.id}-${index}`}
              colSpan={(isMobile ? c.xsGroupedHeaderCellSpan : c.groupedHeaderCellSpan) ?? 1}
              className={classNames(
                "table__data",
                c.label === "Action" && "text-center",
                {
                  "table__data--center": c.center,
                  "table__data--hide-xs": c.xsHide,
                  "tablebox-shadow-row": isExpanded,
                }
              )}
            >
              {c.groupedHeaderCell && c.groupedHeaderCell({...data, isExpanded})}
            </td>
          ) : null
        )}
      </tr>

      {isExpanded &&
        data.children.map((d, i) => (
          <TableRow data={{...d, isChild: true}} columns={columns} index={`${i}-group-${index}`} emphasis />
        ))}
    </React.Fragment>
  );
};

export default Table;
